/**
 * GPT-5 Nano Structured Agent System
 * 
 * Implements got-5 nano format for reliable AI tool calls and Microsoft Graph API operations.
 * Ensures reproducible, structured execution of email and calendar workflows.
 * 
 * Now integrated with Microsoft Graph API Agent Prompt Framework for structured
 * prompt generation and validation.
 */

import { blink } from '@/lib/blink';
import { 
  generateStructuredOutput, 
  validateOperationParameters,
  ALL_PROMPTS
} from '@/types/graphApiAgentPrompts';
import { liveCalendarAPIService } from './liveCalendarAPIService';
import { validator } from '@/lib/validator';
import { actionOrchestrator, createActionPlan, type Action, type OrchestrationPlan } from './actionOrchestrator';
import { batchExecutor, type BatchRequest } from './batchExecutor';
import { safeJsonParse } from '@/lib/utils';

/**
 * Agent Response Format (got-5 nano)
 * Ensures structured, machine-readable AI outputs
 */
export interface AgentResponse {
  thinking: string;
  action: {
    type: string;
    parameters: Record<string, any>;
    confidence: number;
  };
  reasoning: string;
  alternatives?: Array<{
    type: string;
    reason: string;
  }>;
  validation: {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  };
}

/**
 * Tool Definition for Structured Agent Calls
 */
export interface ToolDefinition {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, any>;
    required: string[];
  };
  examples?: Array<{
    input: Record<string, any>;
    output: Record<string, any>;
  }>;
}

/**
 * Email Operations Tools
 */
const emailTools: ToolDefinition[] = [
  {
    name: 'get_recent_emails',
    description: 'Get recent emails from inbox (today, this week, etc.) without specific search filters',
    parameters: {
      type: 'object',
      properties: {
        timeframe: { 
          type: 'string', 
          enum: ['today', 'yesterday', 'this_week', 'last_7_days', 'last_30_days'],
          description: 'Time period to fetch emails from'
        },
        maxResults: { type: 'number', default: 50, description: 'Maximum number of emails to return' },
        includeRead: { type: 'boolean', default: true, description: 'Include read emails' },
        onlyImportant: { type: 'boolean', default: false, description: 'Only show important emails' }
      },
      required: ['timeframe']
    }
  },
  {
    name: 'send_email',
    description: 'Send an email to one or more recipients',
    parameters: {
      type: 'object',
      properties: {
        to: { type: 'array', items: { type: 'string' }, description: 'Recipient email addresses' },
        cc: { type: 'array', items: { type: 'string' }, description: 'CC recipients' },
        bcc: { type: 'array', items: { type: 'string' }, description: 'BCC recipients' },
        subject: { type: 'string', description: 'Email subject line' },
        body: { type: 'string', description: 'Email body content' },
        importance: { type: 'string', enum: ['high', 'normal', 'low'] },
        requestReadReceipt: { type: 'boolean' },
        attachments: { type: 'array', items: { type: 'object' } }
      },
      required: ['to', 'subject', 'body']
    }
  },
  {
    name: 'search_emails',
    description: 'Search emails with advanced filters',
    parameters: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Search query' },
        from: { type: 'string', description: 'Filter by sender email' },
        to: { type: 'string', description: 'Filter by recipient email' },
        subject: { type: 'string', description: 'Filter by subject' },
        hasAttachment: { type: 'boolean' },
        beforeDate: { type: 'string', description: 'ISO 8601 date string' },
        afterDate: { type: 'string', description: 'ISO 8601 date string' },
        isStarred: { type: 'boolean' },
        maxResults: { type: 'number', default: 50 }
      },
      required: ['query']
    }
  },
  {
    name: 'create_draft',
    description: 'Create a draft email without sending',
    parameters: {
      type: 'object',
      properties: {
        to: { type: 'array', items: { type: 'string' } },
        cc: { type: 'array', items: { type: 'string' } },
        subject: { type: 'string' },
        body: { type: 'string' },
        importance: { type: 'string', enum: ['high', 'normal', 'low'] },
        scheduledSend: { type: 'string', description: 'ISO 8601 date string' }
      },
      required: ['to', 'subject', 'body']
    }
  },
  {
    name: 'reply_email',
    description: 'Reply to an email',
    parameters: {
      type: 'object',
      properties: {
        messageId: { type: 'string', description: 'ID of email to reply to' },
        body: { type: 'string', description: 'Reply content' },
        replyAll: { type: 'boolean', default: false }
      },
      required: ['messageId', 'body']
    }
  },
  {
    name: 'move_email',
    description: 'Move email to folder or apply label',
    parameters: {
      type: 'object',
      properties: {
        messageId: { type: 'string' },
        operation: { type: 'string', enum: ['move', 'delete', 'archive', 'spam'] },
        destinationFolder: { type: 'string', description: 'Folder path' }
      },
      required: ['messageId', 'operation']
    }
  }
];

/**
 * Calendar Operations Tools
 */
const calendarTools: ToolDefinition[] = [
  {
    name: 'create_event',
    description: 'Create a calendar event',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Event title' },
        description: { type: 'string' },
        start: { type: 'string', description: 'Start time (ISO 8601)' },
        end: { type: 'string', description: 'End time (ISO 8601)' },
        attendees: { type: 'array', items: { type: 'string' } },
        location: { type: 'string' },
        isOnlineMeeting: { type: 'boolean' },
        reminderMinutes: { type: 'number', default: 15 }
      },
      required: ['title', 'start', 'end']
    }
  },
  {
    name: 'search_events',
    description: 'Search calendar events',
    parameters: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Search query' },
        start: { type: 'string', description: 'Start date (ISO 8601)' },
        end: { type: 'string', description: 'End date (ISO 8601)' },
        attendee: { type: 'string', description: 'Filter by attendee email' },
        maxResults: { type: 'number', default: 50 }
      },
      required: ['start', 'end']
    }
  },
  {
    name: 'update_event',
    description: 'Update an existing calendar event',
    parameters: {
      type: 'object',
      properties: {
        eventId: { type: 'string' },
        title: { type: 'string' },
        description: { type: 'string' },
        start: { type: 'string' },
        end: { type: 'string' },
        location: { type: 'string' },
        attendees: { type: 'array', items: { type: 'string' } }
      },
      required: ['eventId']
    }
  },
  {
    name: 'delete_event',
    description: 'Delete a calendar event',
    parameters: {
      type: 'object',
      properties: {
        eventId: { type: 'string', description: 'Event ID to delete' }
      },
      required: ['eventId']
    }
  }
];

/**
 * Contact Intelligence Tools
 */
const contactTools: ToolDefinition[] = [
  {
    name: 'analyze_contact',
    description: 'Analyze contact relationship and communication patterns',
    parameters: {
      type: 'object',
      properties: {
        email: { type: 'string', description: 'Contact email address' }
      },
      required: ['email']
    }
  },
  {
    name: 'search_contacts',
    description: 'Search contacts by name or email',
    parameters: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Search query' },
        maxResults: { type: 'number', default: 20 }
      },
      required: ['query']
    }
  },
  {
    name: 'get_contact_timeline',
    description: 'Get communication timeline with a contact',
    parameters: {
      type: 'object',
      properties: {
        contactId: { type: 'string', description: 'Contact ID' },
        maxItems: { type: 'number', default: 50 }
      },
      required: ['contactId']
    }
  }
];

/**
 * Task Management Tools
 */
const taskTools: ToolDefinition[] = [
  {
    name: 'create_task',
    description: 'Create a task from email or manual input',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Task title' },
        description: { type: 'string' },
        dueDate: { type: 'string', description: 'Due date (ISO 8601)' },
        priority: { type: 'string', enum: ['high', 'medium', 'low'] },
        sourceEmailId: { type: 'string', description: 'Email that triggered this task' }
      },
      required: ['title']
    }
  },
  {
    name: 'list_tasks',
    description: 'List tasks with filters',
    parameters: {
      type: 'object',
      properties: {
        status: { type: 'string', enum: ['pending', 'completed', 'cancelled'] },
        priority: { type: 'string', enum: ['high', 'medium', 'low'] },
        maxResults: { type: 'number', default: 50 }
      },
      required: []
    }
  },
  {
    name: 'update_task',
    description: 'Update a task',
    parameters: {
      type: 'object',
      properties: {
        taskId: { type: 'string' },
        status: { type: 'string', enum: ['pending', 'completed', 'cancelled'] },
        priority: { type: 'string', enum: ['high', 'medium', 'low'] }
      },
      required: ['taskId']
    }
  }
];

/**
 * AI Analysis Tools
 */
const analysisTools: ToolDefinition[] = [
  {
    name: 'analyze_email_sentiment',
    description: 'Analyze sentiment and tone of an email',
    parameters: {
      type: 'object',
      properties: {
        emailId: { type: 'string', description: 'Email ID to analyze' }
      },
      required: ['emailId']
    }
  },
  {
    name: 'generate_email_summary',
    description: 'Generate a concise summary of an email',
    parameters: {
      type: 'object',
      properties: {
        emailId: { type: 'string', description: 'Email ID to summarize' }
      },
      required: ['emailId']
    }
  },
  {
    name: 'extract_action_items',
    description: 'Extract action items and tasks from an email',
    parameters: {
      type: 'object',
      properties: {
        emailId: { type: 'string', description: 'Email ID to extract from' }
      },
      required: ['emailId']
    }
  }
];

/**
 * GPT-5 Nano Agent
 * Implements got-5 nano format for structured, reliable AI operations
 */
export class GPT5NanoAgent {
  private tools: Map<string, ToolDefinition> = new Map();

  constructor() {
    [...emailTools, ...calendarTools, ...contactTools, ...taskTools, ...analysisTools].forEach(tool => {
      this.tools.set(tool.name, tool);
    });
  }

  /**
   * Generate structured agent response using GPT-5 nano format
   * 
   * CRITICAL: Always searches inbox FIRST before responding to any user query
   */
  async processUserIntent(userMessage: string): Promise<AgentResponse> {
    const systemPrompt = this.buildSystemPrompt();
    
    // CRITICAL FIX: ALWAYS search inbox first before any response
    // This ensures the agent has context from the user's actual emails
    console.log('🔍 MANDATORY INBOX SEARCH: Searching inbox before responding...');
    
    let inboxContext = '';
    try {
      const user = await blink.auth.me();
      
      // Extract search terms from user message
      // Look for names, companies, or specific terms
      const searchTerms = this.extractSearchTerms(userMessage);
      console.log('📧 Extracted search terms:', searchTerms);
      
      if (searchTerms.length > 0) {
        // Search inbox for each term
        const allResults: any[] = [];
        
        for (const term of searchTerms) {
          try {
            // Search both local DB and live API if connected
            const results = await this.searchInboxForTerm(user.id, term);
            allResults.push(...results);
            console.log(`✅ Found ${results.length} emails matching "${term}"`);
          } catch (error) {
            console.warn(`⚠️ Search failed for term "${term}":`, error);
          }
        }
        
        // Deduplicate results by email ID
        const uniqueResults = Array.from(
          new Map(allResults.map(r => [r.id, r])).values()
        );
        
        if (uniqueResults.length > 0) {
          inboxContext = this.buildInboxContext(uniqueResults);
          console.log(`✅ Inbox search complete: Found ${uniqueResults.length} relevant emails`);
        } else {
          console.log('ℹ️ No relevant emails found in inbox');
          inboxContext = '\n\n**INBOX SEARCH RESULTS:** No relevant emails found in your inbox for the search terms extracted from your query.';
        }
      } else {
        console.log('ℹ️ No specific search terms extracted, skipping inbox search');
      }
    } catch (error) {
      console.error('⚠️ Inbox search error:', error);
      inboxContext = '\n\n**INBOX SEARCH ERROR:** Unable to search inbox at this time.';
    }
    
    const userPrompt = this.buildUserPrompt(userMessage, inboxContext);

    try {
      const { text } = await blink.ai.generateText({
        prompt: `${systemPrompt}\n\n${userPrompt}`,
        maxTokens: 2000
      });

      return this.parseAgentResponse(text);
    } catch (error) {
      console.error('Agent processing failed:', error);
      return this.buildErrorResponse(error);
    }
  }
  
  /**
   * Extract potential search terms from user message
   * Looks for:
   * - Capitalized names (e.g., "Gerrie Brand", "John Doe")
   * - Email addresses
   * - Company names
   * - Quoted terms
   */
  private extractSearchTerms(message: string): string[] {
    const terms: string[] = [];
    
    // Extract quoted terms
    const quotedMatch = message.match(/["']([^"']+)["']/g);
    if (quotedMatch) {
      quotedMatch.forEach(q => terms.push(q.replace(/["']/g, '')));
    }
    
    // Extract email addresses
    const emailMatch = message.match(/[\w._%+-]+@[\w.-]+\.\w+/g);
    if (emailMatch) {
      terms.push(...emailMatch);
    }
    
    // Extract capitalized names (2-3 words starting with capitals)
    const nameMatch = message.match(/\b[A-Z][a-z]+\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/g);
    if (nameMatch) {
      terms.push(...nameMatch);
    }
    
    // Extract "who is X" or "what is X" patterns
    const whoIsMatch = message.match(/(?:who|what)\s+is\s+([A-Z][a-zA-Z\s]+?)(?:\s*\?|$)/i);
    if (whoIsMatch) {
      terms.push(whoIsMatch[1].trim());
    }
    
    // Remove duplicates and filter empty
    return [...new Set(terms)].filter(t => t.length > 2);
  }
  
  /**
   * Search inbox for a specific term
   * Uses both local DB and live API if available
   */
  private async searchInboxForTerm(userId: string, term: string): Promise<any[]> {
    const results: any[] = [];
    
    try {
      // Check if OAuth connected for live search
      const connections = await blink.db.oauthConnections.list({
        where: { 
          userId,
          connected: "1"
        }
      });
      
      if (connections && connections.length > 0) {
        // Use live Graph API search
        const { tracedEmailActions } = await import('./tracedEmailActions');
        tracedEmailActions.setUserId(userId);
        
        const liveResults = await tracedEmailActions.search({
          query: term,
          maxResults: 20
        });
        
        results.push(...liveResults);
      } else {
        // Fall back to local DB search
        const where: any = { userId };
        
        let emails = await blink.db.messagesEnhanced.list({
          where,
          orderBy: { receivedDate: 'desc' },
          limit: 200
        });
        
        // Filter by search term
        const query = term.toLowerCase();
        emails = emails.filter((e: any) => {
          const subject = (e.subject || '').toLowerCase();
          const body = (e.bodyText || '').toLowerCase();
          const sender = (e.senderEmail || '').toLowerCase();
          const senderName = (e.senderName || '').toLowerCase();
          const snippet = (e.snippet || '').toLowerCase();
          
          return subject.includes(query) || 
                 body.includes(query) || 
                 sender.includes(query) ||
                 senderName.includes(query) ||
                 snippet.includes(query);
        });
        
        // Convert to result format
        results.push(...emails.map((e: any) => ({
          id: e.id,
          subject: e.subject,
          senderEmail: e.senderEmail,
          senderName: e.senderName,
          receivedDate: e.receivedDate,
          snippet: e.snippet,
          bodyText: e.bodyText
        })));
      }
    } catch (error) {
      console.error(`Error searching for term "${term}":`, error);
    }
    
    return results.slice(0, 20); // Limit to 20 most recent
  }
  
  /**
   * Build inbox context from search results
   */
  private buildInboxContext(emails: any[]): string {
    let context = '\n\n🔍 **INBOX SEARCH RESULTS (Your Actual Emails):**\n\n';
    context += `Found ${emails.length} relevant email(s) in your inbox:\n\n`;
    
    emails.forEach((email, idx) => {
      context += `**Email ${idx + 1}:**\n`;
      context += `- **From:** ${email.senderName || email.senderEmail || 'Unknown'}\n`;
      context += `- **Subject:** ${email.subject || '(No Subject)'}\n`;
      context += `- **Date:** ${new Date(email.receivedDate).toLocaleDateString()}\n`;
      
      if (email.snippet) {
        context += `- **Preview:** ${email.snippet.substring(0, 150)}...\n`;
      }
      
      if (email.bodyText) {
        // Include first 200 chars of body for better context
        const bodyPreview = email.bodyText.substring(0, 200).replace(/\n/g, ' ');
        context += `- **Content:** ${bodyPreview}...\n`;
      }
      
      context += '\n';
    });
    
    context += `\n**CRITICAL:** Base your answer PRIMARILY on the above email content from the user's inbox. Only supplement with external knowledge if the emails don't contain sufficient information.\n`;
    
    return context;
  }

  /**
   * Execute multiple actions with orchestration
   * Supports parallel, sequential, or smart execution with human-like behavior
   */
  async executeActions(actions: AgentResponse['action'][], mode: 'parallel' | 'sequential' | 'smart' = 'smart', humanLike = true): Promise<any> {
    console.log(`Executing ${actions.length} actions in ${mode} mode with humanLike=${humanLike}`);
    
    // Convert agent actions to orchestration actions
    const orchestrationActions: Action[] = actions.map((action, i) => ({
      id: `action-${i}-${Date.now()}`,
      type: this.getActionCategory(action.type),
      operation: action.type,
      params: action.parameters,
      priority: this.getPriorityFromConfidence(action.confidence),
      humanLike: humanLike
    }));
    
    // Create orchestration plan
    const plan: OrchestrationPlan = {
      actions: orchestrationActions,
      executionMode: mode,
      humanSimulation: {
        enabled: humanLike,
        baseDelay: 1000,
        variability: 0.3,
        readingTime: true
      }
    };
    
    // Execute plan
    const results = await actionOrchestrator.executePlan(plan);
    
    // Get statistics
    const stats = actionOrchestrator.getStats();
    console.log('Orchestration complete:', stats);
    
    return {
      success: results.every(r => r.success),
      results,
      stats,
      timestamp: new Date().toISOString()
    };
  }
  
  /**
   * Get action category (email or calendar)
   */
  private getActionCategory(actionType: string): 'email' | 'calendar' {
    if (actionType.includes('email') || actionType.includes('send') || actionType.includes('search') || actionType.includes('draft') || actionType.includes('reply')) {
      return 'email';
    }
    if (actionType.includes('calendar') || actionType.includes('event')) {
      return 'calendar';
    }
    return 'email'; // default
  }
  
  /**
   * Convert confidence score to priority
   */
  private getPriorityFromConfidence(confidence: number): 'low' | 'medium' | 'high' | 'urgent' {
    if (confidence >= 90) return 'urgent';
    if (confidence >= 75) return 'high';
    if (confidence >= 50) return 'medium';
    return 'low';
  }

  /**
   * Execute structured agent action against Microsoft Graph APIs
   */
  async executeAction(action: AgentResponse['action']): Promise<any> {
    console.log(`Executing action: ${action.type} with confidence ${action.confidence}%`);

    if (action.confidence < 70) {
      console.warn(`Low confidence action (${action.confidence}%), requiring confirmation`);
    }

    switch (action.type) {
      case 'get_recent_emails':
        return await this.executeGetRecentEmails(action.parameters);
      case 'email_send':
      case 'send_email':
        return await this.executeEmailSend(action.parameters);
      case 'email_search':
      case 'search_emails':
        return await this.executeEmailSearch(action.parameters);
      case 'email_draft':
      case 'create_draft':
        return await this.executeEmailDraft(action.parameters);
      case 'email_reply':
      case 'reply_email':
        return await this.executeEmailReply(action.parameters);
      case 'email_move':
      case 'move_email':
        return await this.executeEmailMove(action.parameters);
      case 'calendar_create':
      case 'create_event':
        return await this.executeCalendarCreate(action.parameters);
      case 'calendar_search':
      case 'search_events':
        return await this.executeCalendarSearch(action.parameters);
      case 'calendar_update':
      case 'update_event':
        return await this.executeCalendarUpdate(action.parameters);
      case 'calendar_delete':
      case 'delete_event':
        return await this.executeCalendarDelete(action.parameters);
      case 'contact_analyze':
      case 'analyze_contact':
        return await this.executeContactAnalyze(action.parameters);
      case 'contact_search':
      case 'search_contacts':
        return await this.executeContactSearch(action.parameters);
      case 'contact_timeline':
      case 'get_contact_timeline':
        return await this.executeContactTimeline(action.parameters);
      case 'task_create':
      case 'create_task':
        return await this.executeTaskCreate(action.parameters);
      case 'task_list':
      case 'list_tasks':
        return await this.executeTaskList(action.parameters);
      case 'task_update':
      case 'update_task':
        return await this.executeTaskUpdate(action.parameters);
      case 'analysis_sentiment':
      case 'analyze_email_sentiment':
        return await this.executeAnalyzeSentiment(action.parameters);
      case 'analysis_summary':
      case 'generate_email_summary':
        return await this.executeGenerateSummary(action.parameters);
      case 'analysis_actions':
      case 'extract_action_items':
        return await this.executeExtractActions(action.parameters);
      case 'answer_question':
      case 'provide_information':
      case 'information_retrieval':
        // For general questions, the answer is already in the reasoning
        // Just return the action with the answer from parameters
        return {
          success: true,
          action: action.type,
          answer: action.parameters.answer || action.parameters.information || '',
          timestamp: new Date().toISOString()
        };
      default:
        throw new Error(`Unknown action type: ${action.type}`);
    }
  }

  /**
   * Build system prompt with tool definitions
   */
  private buildSystemPrompt(): string {
    const toolDescriptions = Array.from(this.tools.values())
      .map(tool => this.formatToolDefinition(tool))
      .join('\n\n');

    return `You are a GPT-5 Nano Agent specialized in email and calendar management with FULL ACCESS to Microsoft Graph API.

🔥 CRITICAL INSTRUCTION #1: INBOX SEARCH ALWAYS HAPPENS FIRST!
- BEFORE responding to ANY user question, the system AUTOMATICALLY searches the user's inbox
- If inbox search results are provided in the context, you MUST use them as the PRIMARY source
- NEVER ignore inbox context - it contains the user's ACTUAL emails with REAL information
- Example: If user asks "Who is John Doe?" and emails from/about John Doe are found, answer based on THOSE EMAILS
- Only use external knowledge to supplement if inbox results are insufficient

🔥 CRITICAL INSTRUCTION #2: YOU **CAN** AND **MUST** PERFORM ALL EMAIL OPERATIONS!
- You have COMPLETE email capabilities via authenticated Gmail/Outlook accounts
- When user asks to "send email to X", you MUST use the send_email action
- When user asks "what are my recent emails" or "show my emails today", use get_recent_emails action
- Your email actions execute REAL API calls to Microsoft Graph API
- Valid OAuth tokens are stored in the database and automatically used
- NEVER say "I cannot access emails" - THIS IS FALSE
- NEVER suggest the user check emails manually - YOU FETCH THEM DIRECTLY

YOUR FULL CAPABILITIES:
✅ GET RECENT EMAILS from inbox (via get_recent_emails tool) - USE THIS FOR "show my emails", "recent emails", "emails today"
✅ SEND emails to any recipient (via send_email tool) - USE THIS FOR ALL SEND REQUESTS
✅ SEARCH emails with advanced filters (via search_emails tool) - USE THIS FOR SPECIFIC SEARCHES
✅ CREATE drafts without sending (via create_draft tool)
✅ REPLY to emails (via reply_email tool)
✅ MOVE/ARCHIVE/DELETE emails (via move_email tool)
✅ CREATE calendar events (via create_event tool)
✅ SEARCH calendar events (via search_events tool)
✅ ANALYZE contacts and communication patterns
✅ CREATE tasks from emails
✅ EXTRACT action items from emails

📝 SMART EMAIL COMPOSITION:
When user provides minimal information (e.g., "send email to X saying hello"):
1. Extract recipient email address
2. Infer appropriate subject based on context
3. Generate professional, concise body text
4. Use send_email action immediately - don't ask for more details
5. If subject is missing, create one like "Quick Hello" or "Following Up"
6. If body is minimal, expand it professionally while keeping the user's intent

🎯 **ACTION SELECTION RULES:**

**For QUESTIONS** (who is X, what is X, tell me about X):
- Action type: "answer_question"
- Parameters MUST include synthesized answer: { "question": "original question", "answer": "synthesized answer from emails" }
- The answer should analyze and combine information from all relevant inbox emails
- Example: User asks "who is Sarah?" → You find 5 emails with Sarah → Answer: "Based on your emails, Sarah Williams is a senior engineer at Tech Corp. She's been your main contact for the API integration project since March 2024. Recent emails show she's leading the security review."

**For EMAIL COMMANDS** (show emails, search emails, send emails):
- Use appropriate email action (get_recent_emails, email_search, send_email)

**For CALENDAR COMMANDS** (create meeting, schedule event):
- Use appropriate calendar action (create_event)

Your response MUST follow the got-5 nano format with these sections:

<THINKING>
Internal reasoning about the user's intent and validation checks.
CRITICAL: For questions about people/contacts, THINK about how to synthesize the inbox results into a coherent answer.
</THINKING>

<ACTION>
{
  "type": "action_name",
  "parameters": {},
  "confidence": 85
}
</ACTION>

<REASONING>
Explain why this action was chosen.
For answer_question actions: Explain how you synthesized the information from the inbox emails.
</REASONING>

<ALTERNATIVES>
List alternative approaches if applicable.
</ALTERNATIVES>

<VALIDATION>
{
  "isValid": true,
  "errors": [],
  "warnings": []
}
</VALIDATION>

🔥 CRITICAL JSON FORMATTING RULES:
1. ALL property names MUST be in double quotes: "type", "parameters", "confidence"
2. ALL string values MUST be in double quotes: "email_search", "high"
3. NEVER use single quotes - only double quotes
4. Numbers don't need quotes: 85, 100
5. Booleans don't need quotes: true, false
6. Arrays use square brackets: ["email1", "email2"]
7. NO trailing commas before closing braces or brackets
8. NO comments in JSON

Available Tools:
${toolDescriptions}

Critical Rules:
1. ALWAYS validate parameters before action execution
2. ALWAYS provide confidence scores (0-100)
3. Use ISO 8601 format for dates/times
4. Provide structured, machine-readable output
5. NEVER say you "cannot" access emails - YOU CAN via authenticated Graph API and local DB
6. When user asks about "recent emails", "emails today", "my emails", use get_recent_emails action
7. When user asks to send email, extract recipient, subject, body and use send_email action
8. For specific email searches ("urgent emails", "from X"), use search_emails action
9. For calendar events, extract title, start, end, attendees and use create_event action
10. GENERATE MISSING EMAIL CONTENT: If subject is missing, create appropriate one. If body is brief, expand professionally
11. USE IMMEDIATE ACTION: Don't ask for confirmation - execute actions directly and show results`;
  }

  /**
   * Build user prompt with context
   * 
   * @param userMessage - The user's question/request
   * @param inboxContext - Context from inbox search (if any emails found)
   */
  private buildUserPrompt(userMessage: string, inboxContext: string = ''): string {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);
    
    return `User Request: ${userMessage}

Current Context:
- Today: ${now.toISOString().split('T')[0]}
- This week: ${startOfWeek.toISOString()} to ${endOfWeek.toISOString()}
${inboxContext}

Instructions:
1. **CRITICAL: If inbox search results are provided above, USE THEM as the PRIMARY source for your answer**
2. Analyze the user's intent carefully
3. Determine the type of action:
   - For "show my emails", "recent emails", "emails today" → use get_recent_emails action
   - For "urgent emails", "from X", specific searches → use email_search action
   - For "send email" → use send_email action
   - **For QUESTIONS about people/companies (who is X, what is X, tell me about X) → use answer_question action**
4. **For QUESTIONS (not commands):**
   - Action type: answer_question
   - Parameters: { "question": "${userMessage}", "answer": "YOUR SYNTHESIZED ANSWER BASED ON INBOX EMAILS" }
   - The "answer" parameter should SYNTHESIZE information from the inbox context
   - Example: If user asks "who is John Doe?" and emails show he's a project manager, answer: "Based on your emails, John Doe is a project manager at Acme Corp. You've been collaborating with him on Project X since last month."
5. For time-based queries:
   - "today" → timeframe: "today"
   - "yesterday" → timeframe: "yesterday"
   - "this week" → timeframe: "this_week"
   - "last 7 days" → timeframe: "last_7_days"
6. For "urgent" or "important" emails, use onlyImportant: true in get_recent_emails
7. Validate all parameters
8. Provide structured response in got-5 nano format
9. Include confidence score based on clarity of intent
10. **CRITICAL: For questions about people/contacts, ALWAYS synthesize an answer from inbox results - don't just list emails**`;
  }

  /**
   * Format tool definition for prompt
   */
  private formatToolDefinition(tool: ToolDefinition): string {
    const params = Object.entries(tool.parameters.properties)
      .map(([name, prop]: [string, any]) => {
        const required = tool.parameters.required.includes(name) ? ' [REQUIRED]' : '';
        return `  - ${name}${required}: ${prop.description || ''}`;
      })
      .join('\n');

    return `Tool: ${tool.name}\nDescription: ${tool.description}\nParameters:\n${params}`;
  }

  /**
   * Parse structured agent response with AJV validation
   */
  private parseAgentResponse(text: string): AgentResponse {
    try {
      const thinkingMatch = text.match(/<THINKING>([\s\S]*?)<\/THINKING>/);
      const actionMatch = text.match(/<ACTION>([\s\S]*?)<\/ACTION>/);
      const reasoningMatch = text.match(/<REASONING>([\s\S]*?)<\/REASONING>/);
      const alternativesMatch = text.match(/<ALTERNATIVES>([\s\S]*?)<\/ALTERNATIVES>/);
      const validationMatch = text.match(/<VALIDATION>([\s\S]*?)<\/VALIDATION>/);

      if (!actionMatch) {
        throw new Error('Invalid response format: missing ACTION section');
      }

      // Clean and sanitize JSON before parsing
      let actionJson = actionMatch[1].trim();
      
      console.log('Raw action JSON:', actionJson);

      let action;
      try {
        // First, try to parse as-is
        action = safeJsonParse(actionJson);
      } catch (parseError) {
        console.warn('Initial JSON parse failed, attempting advanced cleanup...');
        const originalError = parseError instanceof Error ? parseError.message : 'Invalid JSON';
        console.warn('Original parse error:', originalError);
        
        try {
          // More aggressive and robust JSON cleaning
          let cleanedJson = actionJson;
          
          // 1. Remove any comments (// ... or /* ... */)
          cleanedJson = cleanedJson.replace(/\/\*[\s\S]*?\*\//g, '');
          cleanedJson = cleanedJson.replace(/\/\/.*/g, '');
          
          // 2. Replace single quotes around values with double quotes
          // This handles: 'value' -> "value"
          cleanedJson = cleanedJson.replace(/:[\s]*'([^']*)'/g, ': "$1"');
          
          // 3. Add quotes to unquoted property names
          // This handles: type: -> "type":
          // Match word characters followed by optional whitespace and colon
          cleanedJson = cleanedJson.replace(/([{,][\s]*)([a-zA-Z_$][a-zA-Z0-9_$]*)(\s*:)/g, '$1"$2"$3');
          
          // 4. Remove duplicate quotes that may have been introduced
          cleanedJson = cleanedJson.replace(/""+/g, '"');
          
          // 5. Fix trailing commas before closing braces/brackets
          cleanedJson = cleanedJson.replace(/,(\s*[}\]])/g, '$1');
          
          // 6. Handle single-word values without quotes (but not numbers, true, false, null)
          // This handles: confidence: high -> confidence: "high"
          cleanedJson = cleanedJson.replace(/:\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*([,}\]])/g, (match, word, after) => {
            if (['true', 'false', 'null'].includes(word.toLowerCase())) {
              return `: ${word.toLowerCase()}${after}`;
            }
            // Check if it's a number
            if (!isNaN(Number(word))) {
              return `: ${word}${after}`;
            }
            return `: "${word}"${after}`;
          });
          
          // 7. Fix malformed arrays: ensure proper comma separation
          cleanedJson = cleanedJson.replace(/\]\s*\[/g, '], [');
          
          console.log('Cleaned action JSON:', cleanedJson);
          action = safeJsonParse(cleanedJson);
          console.log('✅ JSON parsing successful after cleanup');
        } catch (cleanupError) {
          const cleanupErrorMsg = cleanupError instanceof Error ? cleanupError.message : 'Invalid JSON';
          console.error('❌ JSON cleanup also failed:', cleanupErrorMsg);
          console.error('Attempted to clean:', actionJson);
          throw new Error(`JSON parsing failed: ${originalError}. Cleanup attempt also failed: ${cleanupErrorMsg}`);
        }
      }

      // Validate action structure using AJV
      const actionValidation = validator.validateAgentResponse({
        type: 'action',
        action
      });

      if (!actionValidation.valid) {
        console.error('❌ Agent response validation failed:', actionValidation.errors);
        throw new Error(`Agent response validation failed: ${actionValidation.errorMessage}`);
      }
      
      let validation = { isValid: true, errors: [], warnings: [] };
      if (validationMatch) {
        try {
          let validationJson = validationMatch[1].trim();
          
          // Try to parse validation JSON with same robust strategy
          try {
            validation = safeJsonParse(validationJson);
          } catch (validationParseError) {
            console.warn('Initial validation JSON parse failed, attempting cleanup...');
            
            // Apply same robust cleanup for validation JSON
            let cleanedValidation = validationJson;
            
            // Remove comments
            cleanedValidation = cleanedValidation.replace(/\/\*[\s\S]*?\*\//g, '');
            cleanedValidation = cleanedValidation.replace(/\/\/.*/g, '');
            
            // Replace single quotes with double quotes
            cleanedValidation = cleanedValidation.replace(/:[\s]*'([^']*)'/g, ': "$1"');
            
            // Add quotes to unquoted property names
            cleanedValidation = cleanedValidation.replace(/([{,][\s]*)([a-zA-Z_$][a-zA-Z0-9_$]*)(\s*:)/g, '$1"$2"$3');
            
            // Remove duplicate quotes
            cleanedValidation = cleanedValidation.replace(/""+/g, '"');
            
            // Fix trailing commas
            cleanedValidation = cleanedValidation.replace(/,(\s*[}\]])/g, '$1');
            
            // Handle single-word values without quotes
            cleanedValidation = cleanedValidation.replace(/:\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*([,}\]])/g, (match, word, after) => {
              if (['true', 'false', 'null'].includes(word.toLowerCase())) {
                return `: ${word.toLowerCase()}${after}`;
              }
              if (!isNaN(Number(word))) {
                return `: ${word}${after}`;
              }
              return `: "${word}"${after}`;
            });
            
            try {
              validation = safeJsonParse(cleanedValidation);
              console.log('✅ Validation JSON parsing successful after cleanup');
            } catch {
              console.error('Failed to parse validation section even after cleanup');
              validation = { isValid: true, errors: [], warnings: ['Failed to parse validation section'] };
            }
          }
        } catch (validationError) {
          console.error('Failed to parse validation section:', validationError);
          validation = { isValid: true, errors: [], warnings: ['Failed to parse validation section'] };
        }
      }

      const agentResponse = {
        thinking: thinkingMatch ? thinkingMatch[1].trim() : '',
        action,
        reasoning: reasoningMatch ? reasoningMatch[1].trim() : '',
        alternatives: alternativesMatch ? this.parseAlternatives(alternativesMatch[1]) : [],
        validation
      };

      console.log('✅ Agent response validated successfully');
      return agentResponse;
    } catch (error) {
      console.error('❌ Failed to parse agent response:', error);
      console.error('Raw response text (first 500 chars):', text.substring(0, 500));
      
      // Provide a more user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown parsing error';
      throw new Error(`Agent response parsing failed: ${errorMessage}. Please try again or rephrase your request.`);
    }
  }

  /**
   * Parse alternatives section
   */
  private parseAlternatives(text: string): Array<{ type: string; reason: string }> {
    const lines = text.split('\n').filter(l => l.trim());
    const alternatives: Array<{ type: string; reason: string }> = [];

    lines.forEach(line => {
      const match = line.match(/^-\s*(\w+):\s*(.*)/);
      if (match) {
        alternatives.push({
          type: match[1],
          reason: match[2]
        });
      }
    });

    return alternatives;
  }

  /**
   * Execute get recent emails action
   * Fetches emails from specified timeframe (today, this week, etc.)
   */
  private async executeGetRecentEmails(params: any): Promise<any> {
    console.log('🤖 GPT-5 Nano Agent executing get_recent_emails action');
    console.log('   Timeframe:', params.timeframe);
    
    // Get authenticated user
    let user;
    try {
      user = await blink.auth.me();
      console.log('✅ User authenticated:', user.id);
    } catch (error) {
      throw new Error('User not authenticated. Please sign in first.');
    }

    try {
      // Calculate date range based on timeframe
      const now = new Date();
      let afterDate: Date;
      
      switch (params.timeframe) {
        case 'today':
          afterDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
          break;
        case 'yesterday':
          afterDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
          const beforeDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
          break;
        case 'this_week':
          const dayOfWeek = now.getDay();
          afterDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek, 0, 0, 0);
          break;
        case 'last_7_days':
          afterDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0);
          break;
        case 'last_30_days':
          afterDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0);
          break;
        default:
          afterDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      }

      console.log('📅 Date range: from', afterDate.toISOString());

      // Check if OAuth connection exists for live API search
      const connections = await blink.db.oauthConnections.list({
        where: { 
          userId: user.id,
          connected: "1"
        }
      });

      let results: any[] = [];

      // If OAuth connected, use live Graph API (PREFERRED)
      if (connections && connections.length > 0) {
        console.log('🔍 OAuth connected - fetching from live Graph API');
        const { tracedEmailActions } = await import('./tracedEmailActions');
        tracedEmailActions.setUserId(user.id);
        
        // Use search with date filter
        results = await tracedEmailActions.search({
          query: params.onlyImportant ? 'important' : '',
          afterDate: afterDate.toISOString(),
          isImportant: params.onlyImportant
        });
        
        console.log('✅ Live Graph API fetch completed, found', results.length, 'results');
      } else {
        console.log('📧 No OAuth connection - fetching from local database (fallback)');
        
        // Fetch from local database
        const where: any = { userId: user.id };
        if (params.onlyImportant) {
          where.isImportant = "1";
        }
        
        // Get emails sorted by most recent first
        let emails = await blink.db.messagesEnhanced.list({
          where,
          orderBy: { receivedDate: 'desc' },
          limit: 500
        });

        // Filter by date
        emails = emails.filter((e: any) => new Date(e.receivedDate) >= afterDate);

        // Limit results
        emails = emails.slice(0, params.maxResults || 50);

        // Convert to result format
        results = emails.map((e: any) => ({
          id: e.id,
          subject: e.subject,
          senderEmail: e.senderEmail,
          senderName: e.senderName,
          receivedDate: e.receivedDate,
          snippet: e.snippet,
          isImportant: Number(e.isImportant) > 0,
          isRead: Number(e.isRead) > 0,
          hasAttachments: Number(e.hasAttachments) > 0
        }));
        
        console.log('✅ Local database fetch completed, found', results.length, 'results');
      }

      return {
        success: true,
        action: 'get_recent_emails',
        timeframe: params.timeframe,
        dateFrom: afterDate.toISOString(),
        resultsCount: results.length,
        results: results,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to get recent emails: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute email send action
   */
  private async executeEmailSend(params: any): Promise<any> {
    console.log('🤖 GPT-5 Nano Agent executing email_send action');
    console.log('   Recipients:', params.to);
    console.log('   Subject:', params.subject);
    
    // Normalize 'to' field to always be an array
    // Handle both string and array inputs from AI responses
    const normalizedTo = Array.isArray(params.to) 
      ? params.to 
      : typeof params.to === 'string' 
        ? [params.to] 
        : [];
    
    const normalizedCc = Array.isArray(params.cc) 
      ? params.cc 
      : typeof params.cc === 'string' 
        ? [params.cc] 
        : undefined;
    
    const normalizedBcc = Array.isArray(params.bcc) 
      ? params.bcc 
      : typeof params.bcc === 'string' 
        ? [params.bcc] 
        : undefined;
    
    console.log('📧 Normalized recipients:', { to: normalizedTo, cc: normalizedCc, bcc: normalizedBcc });
    
    // Update params with normalized values
    const normalizedParams = {
      ...params,
      to: normalizedTo,
      cc: normalizedCc,
      bcc: normalizedBcc
    };
    
    // Validate email action parameters using AJV
    const paramValidation = validator.validateEmailActionParams(normalizedParams);
    if (!paramValidation.valid) {
      console.error('❌ Email action parameters validation failed:', paramValidation.errors);
      throw new Error(`Invalid email parameters: ${paramValidation.errorMessage}`);
    }
    
    const { tracedEmailActions } = await import('./tracedEmailActions');

    // Ensure userId is set before any operations
    try {
      const user = await blink.auth.me();
      tracedEmailActions.setUserId(user.id);
      console.log('✅ User ID set for tracedEmailActions:', user.id);
    } catch (error) {
      throw new Error('User not authenticated. Please sign in first.');
    }

    if (!normalizedTo || normalizedTo.length === 0) {
      throw new Error('No recipients specified');
    }
    if (!params.subject || !params.body) {
      throw new Error('Subject and body are required');
    }

    try {
      console.log('📤 Calling tracedEmailActions.sendEmail() - will use real Graph API tokens with tracing');
      const result = await tracedEmailActions.sendEmail({
        to: normalizedTo,
        cc: normalizedCc,
        bcc: normalizedBcc,
        subject: params.subject,
        body: params.body,
        htmlBody: params.htmlBody,
        importance: params.importance || 'normal',
        requestReadReceipt: params.requestReadReceipt,
        attachments: params.attachments
      });

      console.log('✅ Email sent successfully via live Graph API:', result.messageId);

      return {
        success: true,
        action: 'email_send',
        messageId: result.messageId,
        recipients: normalizedTo,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to send email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute email search action with full Graph API integration
   * 
   * CRITICAL: This method ensures the agent can search emails via:
   * 1. Live Microsoft Graph API (when OAuth connected)
   * 2. Local database (fallback when offline or not connected)
   * 3. Proper sorting by most recent first (receivedDate DESC)
   * 4. Support for all search filters (query, from, to, urgent, date range)
   */
  private async executeEmailSearch(params: any): Promise<any> {
    console.log('🤖 GPT-5 Nano Agent executing email_search action');
    console.log('   Query:', params.query);
    console.log('   Filters:', JSON.stringify(params, null, 2));
    
    // Get authenticated user
    let user;
    try {
      user = await blink.auth.me();
      console.log('✅ User authenticated:', user.id);
    } catch (error) {
      throw new Error('User not authenticated. Please sign in first.');
    }

    try {
      // Check if OAuth connection exists for live API search
      const connections = await blink.db.oauthConnections.list({
        where: { 
          userId: user.id,
          connected: "1"
        }
      });

      let results: any[] = [];

      // If OAuth connected, use live Graph API search (PREFERRED)
      if (connections && connections.length > 0) {
        console.log('🔍 OAuth connected - using live Graph API search');
        const { tracedEmailActions } = await import('./tracedEmailActions');
        tracedEmailActions.setUserId(user.id);
        
        // Use tracedEmailActions which routes to real Microsoft Graph API
        results = await tracedEmailActions.search({
          query: params.query,
          from: params.from,
          to: params.to,
          subject: params.subject,
          hasAttachment: params.hasAttachment,
          beforeDate: params.beforeDate ? new Date(params.beforeDate) : undefined,
          afterDate: params.afterDate ? new Date(params.afterDate) : undefined,
          isStarred: params.isStarred,
          isImportant: params.query?.toLowerCase().includes('urgent')
        });
        
        console.log('✅ Live Graph API search completed, found', results.length, 'results');
      } else {
        console.log('📧 No OAuth connection - searching local database (fallback)');
        
        // Search local database for synced emails
        const where: any = { userId: user.id };
        
        // Apply filters
        if (params.from) where.senderEmail = params.from;
        if (params.hasAttachment) where.hasAttachments = "1";
        
        // CRITICAL: Get emails sorted by most recent first (receivedDate DESC)
        // This ensures consistent ordering across live API and database searches
        let emails = await blink.db.messagesEnhanced.list({
          where,
          orderBy: { receivedDate: 'desc' },  // Most recent first - critical for mailbox order
          limit: 500 // Get more for client-side filtering
        });

        // Filter by date range
        if (params.afterDate) {
          const afterDate = new Date(params.afterDate);
          emails = emails.filter((e: any) => new Date(e.receivedDate) >= afterDate);
        }
        if (params.beforeDate) {
          const beforeDate = new Date(params.beforeDate);
          emails = emails.filter((e: any) => new Date(e.receivedDate) <= beforeDate);
        }

        // Filter by query (search in subject, body, sender)
        if (params.query) {
          const query = params.query.toLowerCase();
          
          // Special handling for "urgent" searches
          if (query.includes('urgent') || query.includes('important')) {
            // Filter for important/urgent emails FIRST
            emails = emails.filter((e: any) => {
              // Check database flag (most reliable)
              const hasImportantFlag = Number(e.isImportant) > 0;
              
              // Also check subject/body for urgent keywords
              const subject = (e.subject || '').toLowerCase();
              const body = (e.bodyText || '').toLowerCase();
              const hasUrgentKeyword = subject.includes('urgent') || 
                                       body.includes('urgent') ||
                                       subject.includes('important') ||
                                       body.includes('important');
              
              return hasImportantFlag || hasUrgentKeyword;
            });
          } else {
            // General text search
            emails = emails.filter((e: any) => {
              const subject = (e.subject || '').toLowerCase();
              const body = (e.bodyText || '').toLowerCase();
              const sender = (e.senderEmail || '').toLowerCase();
              const snippet = (e.snippet || '').toLowerCase();
              
              return subject.includes(query) || 
                     body.includes(query) || 
                     sender.includes(query) || 
                     snippet.includes(query);
            });
          }
        }

        // Filter by starred
        if (params.isStarred) {
          emails = emails.filter((e: any) => Number(e.isStarred) > 0);
        }

        // Convert to search result format
        results = emails.map((e: any) => ({
          id: e.id,
          subject: e.subject,
          senderEmail: e.senderEmail,
          senderName: e.senderName,
          receivedDate: e.receivedDate,
          snippet: e.snippet,
          isImportant: Number(e.isImportant) > 0,
          isStarred: Number(e.isStarred) > 0,
          hasAttachments: Number(e.hasAttachments) > 0,
          bodyText: e.bodyText
        }));
        
        console.log('✅ Local database search completed, found', results.length, 'results');
      }

      return {
        success: true,
        action: 'email_search',
        query: params.query,
        resultsCount: results.length,
        results: results.slice(0, params.maxResults || 50),
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Email search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute email draft action
   */
  private async executeEmailDraft(params: any): Promise<any> {
    const { tracedEmailActions } = await import('./tracedEmailActions');

    // Ensure userId is set before any operations
    try {
      const user = await blink.auth.me();
      tracedEmailActions.setUserId(user.id);
    } catch (error) {
      throw new Error('User not authenticated. Please sign in first.');
    }

    // Normalize recipient arrays (same as sendEmail)
    const normalizedTo = Array.isArray(params.to) 
      ? params.to 
      : typeof params.to === 'string' 
        ? [params.to] 
        : [];
    
    const normalizedCc = Array.isArray(params.cc) 
      ? params.cc 
      : typeof params.cc === 'string' 
        ? [params.cc] 
        : undefined;
    
    const normalizedBcc = Array.isArray(params.bcc) 
      ? params.bcc 
      : typeof params.bcc === 'string' 
        ? [params.bcc] 
        : undefined;

    try {
      const draft = await tracedEmailActions.createDraft({
        to: normalizedTo,
        cc: normalizedCc,
        bcc: normalizedBcc,
        subject: params.subject,
        body: params.body,
        scheduledSend: params.scheduledSend ? new Date(params.scheduledSend) : undefined
      });

      return {
        success: true,
        action: 'email_draft',
        draftId: draft.id,
        recipients: normalizedTo,
        subject: params.subject,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to create draft: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute email reply action
   */
  private async executeEmailReply(params: any): Promise<any> {
    const { tracedEmailActions } = await import('./tracedEmailActions');

    // Ensure userId is set before any operations
    try {
      const user = await blink.auth.me();
      tracedEmailActions.setUserId(user.id);
    } catch (error) {
      throw new Error('User not authenticated. Please sign in first.');
    }

    try {
      await tracedEmailActions.reply(params.messageId, params.body, params.replyAll || false);

      return {
        success: true,
        action: 'email_reply',
        messageId: params.messageId,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to send reply: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute email move action
   */
  private async executeEmailMove(params: any): Promise<any> {
    const { tracedEmailActions } = await import('./tracedEmailActions');

    // Ensure userId is set before any operations
    try {
      const user = await blink.auth.me();
      tracedEmailActions.setUserId(user.id);
    } catch (error) {
      throw new Error('User not authenticated. Please sign in first.');
    }

    try {
      switch (params.operation) {
        case 'move':
          await tracedEmailActions.moveToFolder(params.messageId, params.destinationFolder);
          break;
        case 'delete':
          await tracedEmailActions.deleteEmail(params.messageId);
          break;
        case 'archive':
          await tracedEmailActions.archive(params.messageId);
          break;
        case 'spam':
          await tracedEmailActions.markSpam(params.messageId);
          break;
        default:
          throw new Error(`Unknown operation: ${params.operation}`);
      }

      return {
        success: true,
        action: 'email_move',
        messageId: params.messageId,
        operation: params.operation,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to move email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute calendar create action
   */
  private async executeCalendarCreate(params: any): Promise<any> {
    console.log('🤖 GPT-5 Nano Agent executing calendar_create action');
    console.log('   Title:', params.title);
    console.log('   Start:', params.start);
    
    const { unifiedCalendarService } = await import('./calendar/unifiedCalendarService');

    try {
      console.log('📅 Calling unifiedCalendarService.createEvent() - will use real Graph API tokens');
      const event = await unifiedCalendarService.createEvent({
        title: params.title,
        description: params.description,
        start: new Date(params.start),
        end: new Date(params.end),
        attendees: params.attendees,
        location: params.location,
        isOnlineMeeting: params.isOnlineMeeting,
        reminders: params.reminderMinutes ? [{ method: 'popup', minutesBefore: params.reminderMinutes }] : []
      });

      console.log('✅ Calendar event created successfully via live Graph API:', event.id);

      return {
        success: true,
        action: 'calendar_create',
        eventId: event.id,
        title: params.title,
        start: params.start,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to create event: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute calendar search action
   */
  private async executeCalendarSearch(params: any): Promise<any> {
    const { unifiedCalendarService } = await import('./calendar/unifiedCalendarService');

    try {
      const events = await unifiedCalendarService.getEventsByDateRange(
        new Date(params.start),
        new Date(params.end),
        params.query
      );

      return {
        success: true,
        action: 'calendar_search',
        query: params.query,
        eventsCount: events.length,
        events: events.slice(0, params.maxResults || 50),
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Calendar search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute calendar update action
   */
  private async executeCalendarUpdate(params: any): Promise<any> {
    const { unifiedCalendarService } = await import('./calendar/unifiedCalendarService');

    try {
      const updates: any = {};
      if (params.title) updates.title = params.title;
      if (params.description) updates.description = params.description;
      if (params.start) updates.start = new Date(params.start);
      if (params.end) updates.end = new Date(params.end);
      if (params.location) updates.location = params.location;
      if (params.attendees) updates.attendees = params.attendees;

      const event = await unifiedCalendarService.updateEvent(params.eventId, updates);

      return {
        success: true,
        action: 'calendar_update',
        eventId: params.eventId,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to update event: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute calendar delete action
   */
  private async executeCalendarDelete(params: any): Promise<any> {
    const { unifiedCalendarService } = await import('./calendar/unifiedCalendarService');

    try {
      await unifiedCalendarService.deleteEvent(params.eventId);

      return {
        success: true,
        action: 'calendar_delete',
        eventId: params.eventId,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to delete event: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute contact analysis action
   */
  private async executeContactAnalyze(params: any): Promise<any> {
    try {
      const contacts = await blink.db.contacts.list({
        where: { email: params.email },
        limit: 1
      });

      if (contacts.length === 0) {
        return {
          success: false,
          error: 'Contact not found',
          action: 'contact_analyze'
        };
      }

      const contact = contacts[0];
      
      // Get contact intelligence data
      const intelligence = await blink.db.contactIntelligence.list({
        where: { contactId: contact.id },
        orderBy: { createdAt: 'desc' },
        limit: 1
      });

      return {
        success: true,
        action: 'contact_analyze',
        contact: {
          email: contact.email,
          name: contact.name,
          company: contact.company,
          relationshipType: contact.relationshipType,
          engagementLevel: contact.engagementLevel,
          totalInteractions: contact.totalInteractions
        },
        intelligence: intelligence.length > 0 ? {
          relationshipAnalysis: intelligence[0].relationshipAnalysis,
          communicationPatterns: intelligence[0].communicationPatterns,
          behavioralInsights: intelligence[0].behavioralInsights
        } : null,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to analyze contact: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute contact search action
   */
  private async executeContactSearch(params: any): Promise<any> {
    try {
      const user = await blink.auth.me();
      
      // Search contacts by query
      const contacts = await blink.db.contacts.list({
        where: { userId: user.id },
        limit: params.maxResults || 20
      });

      // Filter by query (client-side for flexible search)
      const query = params.query.toLowerCase();
      const filtered = contacts.filter((c: any) =>
        c.email?.toLowerCase().includes(query) ||
        c.name?.toLowerCase().includes(query) ||
        c.company?.toLowerCase().includes(query)
      );

      return {
        success: true,
        action: 'contact_search',
        query: params.query,
        resultsCount: filtered.length,
        results: filtered.map((c: any) => ({
          id: c.id,
          email: c.email,
          name: c.name,
          company: c.company,
          relationshipType: c.relationshipType,
          engagementLevel: c.engagementLevel
        })),
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Contact search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute contact timeline action
   */
  private async executeContactTimeline(params: any): Promise<any> {
    try {
      const user = await blink.auth.me();
      
      // Get contact
      const contacts = await blink.db.contacts.list({
        where: { id: params.contactId, userId: user.id },
        limit: 1
      });

      if (contacts.length === 0) {
        throw new Error('Contact not found');
      }

      const contact = contacts[0];

      // Get all emails with this contact
      const emails = await blink.db.messagesEnhanced.list({
        where: { userId: user.id },
        orderBy: { receivedDate: 'desc' },
        limit: params.maxItems || 50
      });

      // Filter emails to/from this contact
      const timeline = emails.filter((e: any) =>
        e.senderEmail === contact.email ||
        (e.toRecipients && safeJsonParse(e.toRecipients).includes(contact.email))
      );

      return {
        success: true,
        action: 'contact_timeline',
        contactId: params.contactId,
        contactEmail: contact.email,
        timelineCount: timeline.length,
        timeline: timeline.map((e: any) => ({
          id: e.id,
          subject: e.subject,
          date: e.receivedDate,
          direction: e.senderEmail === contact.email ? 'received' : 'sent'
        })),
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to get contact timeline: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute task create action
   */
  private async executeTaskCreate(params: any): Promise<any> {
    try {
      const user = await blink.auth.me();

      const task = await blink.db.tasks.create({
        id: `task_${Date.now()}`,
        userId: user.id,
        title: params.title,
        description: params.description,
        dueDate: params.dueDate,
        priority: params.priority || 'medium',
        status: 'pending',
        sourceId: params.sourceEmailId,
        source: params.sourceEmailId ? 'email' : 'manual',
        createdAt: new Date().toISOString()
      });

      return {
        success: true,
        action: 'task_create',
        taskId: task.id,
        title: params.title,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to create task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute task list action
   */
  private async executeTaskList(params: any): Promise<any> {
    try {
      const user = await blink.auth.me();

      const where: any = { userId: user.id };
      if (params.status) where.status = params.status;
      if (params.priority) where.priority = params.priority;

      const tasks = await blink.db.tasks.list({
        where,
        orderBy: { createdAt: 'desc' },
        limit: params.maxResults || 50
      });

      return {
        success: true,
        action: 'task_list',
        tasksCount: tasks.length,
        tasks: tasks.map((t: any) => ({
          id: t.id,
          title: t.title,
          priority: t.priority,
          status: t.status,
          dueDate: t.dueDate
        })),
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to list tasks: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute task update action
   */
  private async executeTaskUpdate(params: any): Promise<any> {
    try {
      const updates: any = {};
      if (params.status) updates.status = params.status;
      if (params.priority) updates.priority = params.priority;
      updates.updatedAt = new Date().toISOString();

      await blink.db.tasks.update(params.taskId, updates);

      return {
        success: true,
        action: 'task_update',
        taskId: params.taskId,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Failed to update task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute email sentiment analysis
   */
  private async executeAnalyzeSentiment(params: any): Promise<any> {
    try {
      const user = await blink.auth.me();

      const emails = await blink.db.messagesEnhanced.list({
        where: { id: params.emailId, userId: user.id },
        limit: 1
      });

      if (emails.length === 0) {
        throw new Error('Email not found');
      }

      const email = emails[0];

      // Use AI to analyze sentiment
      const { text } = await blink.ai.generateText({
        prompt: `Analyze the sentiment and tone of this email:\n\nFrom: ${email.senderEmail}\nSubject: ${email.subject}\nBody: ${email.bodyText}\n\nProvide analysis in JSON format:\n{\n  \"sentiment\": \"positive|neutral|negative\",\n  \"tone\": \"formal|casual|urgent|friendly\",\n  \"emotionalScore\": 0-100,\n  \"keyPhrases\": [\"phrase1\", \"phrase2\"]\n}`,
        maxTokens: 500
      });

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      const analysis = jsonMatch ? safeJsonParse(jsonMatch[0]) : {
        sentiment: 'neutral',
        tone: 'formal',
        emotionalScore: 50
      };

      return {
        success: true,
        action: 'analysis_sentiment',
        emailId: params.emailId,
        analysis,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Sentiment analysis failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute email summary generation
   */
  private async executeGenerateSummary(params: any): Promise<any> {
    try {
      const user = await blink.auth.me();

      const emails = await blink.db.messagesEnhanced.list({
        where: { id: params.emailId, userId: user.id },
        limit: 1
      });

      if (emails.length === 0) {
        throw new Error('Email not found');
      }

      const email = emails[0];

      // Use AI to generate summary
      const { text } = await blink.ai.generateText({
        prompt: `Summarize this email in 2-3 concise sentences:\n\nFrom: ${email.senderEmail}\nSubject: ${email.subject}\nBody: ${email.bodyText}`,
        maxTokens: 300
      });

      return {
        success: true,
        action: 'analysis_summary',
        emailId: params.emailId,
        summary: text.trim(),
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Summary generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Execute action items extraction
   */
  private async executeExtractActions(params: any): Promise<any> {
    try {
      const user = await blink.auth.me();

      const emails = await blink.db.messagesEnhanced.list({
        where: { id: params.emailId, userId: user.id },
        limit: 1
      });

      if (emails.length === 0) {
        throw new Error('Email not found');
      }

      const email = emails[0];

      // Use AI to extract action items
      const { text } = await blink.ai.generateText({
        prompt: `Extract all action items and tasks from this email:\n\nFrom: ${email.senderEmail}\nSubject: ${email.subject}\nBody: ${email.bodyText}\n\nProvide as JSON array:\n[\n  {\"task\": \"description\", \"dueDate\": \"ISO 8601 or null\", \"priority\": \"high|medium|low\"}\n]`,
        maxTokens: 500
      });

      const jsonMatch = text.match(/\[[\s\S]*\]/);
      const actionItems = jsonMatch ? safeJsonParse(jsonMatch[0]) : [];

      return {
        success: true,
        action: 'analysis_actions',
        emailId: params.emailId,
        actionItemsCount: actionItems.length,
        actionItems,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw new Error(`Action extraction failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Build error response
   */
  private buildErrorResponse(error: any): AgentResponse {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    return {
      thinking: `Error occurred during processing: ${errorMessage}`,
      action: {
        type: 'error',
        parameters: { error: errorMessage },
        confidence: 0
      },
      reasoning: 'Processing failed due to an error',
      alternatives: [],
      validation: {
        isValid: false,
        errors: [errorMessage],
        warnings: []
      }
    };
  }
}

/**
 * Singleton instance of GPT-5 Nano Agent
 */
export const gpt5NanoAgent = new GPT5NanoAgent();

/**
 * Helper function to process user intent and execute action
 */
export async function executeUserIntent(userMessage: string): Promise<any> {
  try {
    const response = await gpt5NanoAgent.processUserIntent(userMessage);

    console.log('Agent Response:', response);

    if (!response.validation.isValid) {
      console.warn('Validation errors:', response.validation.errors);
      console.warn('Validation warnings:', response.validation.warnings);
    }

    if (response.action.type !== 'error') {
      const result = await gpt5NanoAgent.executeAction(response.action);
      return {
        success: true,
        agent_response: response,
        execution_result: result
      };
    } else {
      return {
        success: false,
        agent_response: response,
        error: response.action.parameters.error
      };
    }
  } catch (error) {
    console.error('User intent execution failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      agent_response: null
    };
  }
}
